import React, { useEffect, useRef, useState, useCallback } from 'react';
import rrwebPlayer from 'rrweb-player';
import type { eventWithTime } from 'rrweb';
import 'rrweb-player/dist/style.css';

interface SyncedDomPlayerProps {
  sourceUrl: string;
  /** Current audio playback time in seconds (session-level). */
  audioCurrentTime: number;
  /** Whether the session audio is currently playing. */
  isPlaying: boolean;
  /** When this media item starts in the session audio timeline (seconds). */
  segmentStartOffset: number;
  /** Offset inside the rrweb slice where the actual segment begins (seconds). */
  recordingStartOffsetSeconds?: number;
  className?: string;
}

type DomRecordingPayload =
  | eventWithTime[]
  | { events?: eventWithTime[] }
  | { data?: eventWithTime[] };

// Module-level cache to avoid re-fetching the same recording
const eventsCache = new Map<string, eventWithTime[]>();
const MAX_CACHE_SIZE = 10;

function parsePayload(payload: DomRecordingPayload): eventWithTime[] {
  if (Array.isArray(payload)) return payload;
  if (payload?.events && Array.isArray(payload.events)) return payload.events;
  if (payload?.data && Array.isArray(payload.data)) return payload.data;
  return [];
}

const SyncedDomPlayer: React.FC<SyncedDomPlayerProps> = ({
  sourceUrl,
  audioCurrentTime,
  isPlaying,
  segmentStartOffset,
  recordingStartOffsetSeconds = 0,
  className = '',
}) => {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const playerRef = useRef<rrwebPlayer | null>(null);
  const [events, setEvents] = useState<eventWithTime[] | null>(null);
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading');
  const recordingMetaRef = useRef({ width: 1280, height: 720 });
  const initialSizeRef = useRef<{ width: number; height: number } | null>(null);
  const lastSyncMs = useRef(0);
  const wasPlayingRef = useRef(false);

  // Fetch events
  useEffect(() => {
    let mounted = true;
    setStatus('loading');

    const cached = eventsCache.get(sourceUrl);
    if (cached) {
      // Extract meta
      const meta = cached.find((e) => e.type === 4);
      if (meta?.data?.width && meta?.data?.height) {
        recordingMetaRef.current = { width: meta.data.width, height: meta.data.height };
      }
      setEvents(cached);
      setStatus('ready');
      return;
    }

    fetch(sourceUrl)
      .then((r) => {
        if (!r.ok) throw new Error('Failed to fetch');
        return r.json() as Promise<DomRecordingPayload>;
      })
      .then((payload) => {
        if (!mounted) return;
        const parsed = parsePayload(payload);
        if (!parsed.length) {
          setStatus('error');
          return;
        }
        if (eventsCache.size >= MAX_CACHE_SIZE) {
          const oldest = eventsCache.keys().next().value;
          if (oldest) eventsCache.delete(oldest);
        }
        eventsCache.set(sourceUrl, parsed);
        const meta = parsed.find((e) => e.type === 4);
        if (meta?.data?.width && meta?.data?.height) {
          recordingMetaRef.current = { width: meta.data.width, height: meta.data.height };
        }
        setEvents(parsed);
        setStatus('ready');
      })
      .catch(() => {
        if (mounted) setStatus('error');
      });

    return () => { mounted = false; };
  }, [sourceUrl]);

  // Apply CSS transform scaling to fit container
  const applyScale = useCallback((containerWidth: number, containerHeight: number) => {
    const container = containerRef.current;
    const wrapper = wrapperRef.current;
    const initial = initialSizeRef.current;
    if (!container || !wrapper || !initial) return;

    const scaleX = containerWidth / initial.width;
    const scaleY = containerHeight / initial.height;
    const scale = Math.min(scaleX, scaleY);

    const scaledW = Math.ceil(initial.width * scale);
    const scaledH = Math.ceil(initial.height * scale);

    wrapper.style.width = `${scaledW}px`;
    wrapper.style.height = `${scaledH}px`;
    wrapper.style.overflow = 'hidden';

    container.style.transformOrigin = 'top left';
    container.style.transform = `scale(${scale})`;
    container.style.width = `${initial.width}px`;
    container.style.height = `${initial.height}px`;
  }, []);

  // Create player when events are ready
  useEffect(() => {
    if (!containerRef.current || !wrapperRef.current || !events?.length) return;

    // Destroy existing
    if (playerRef.current) {
      try {
        (playerRef.current as unknown as { destroy?: () => void }).destroy?.();
      } catch (e) { console.warn('rrweb player destroy failed:', e); }
      playerRef.current = null;
    }
    containerRef.current.innerHTML = '';
    containerRef.current.style.transform = '';
    initialSizeRef.current = null;

    const { width: recW, height: recH } = recordingMetaRef.current;
    const recAspect = recW / recH;

    // Use a reasonable initial size (will be scaled by ResizeObserver)
    let initialW = Math.min(recW, 960);
    let initialH = initialW / recAspect;
    if (initialH > 540) {
      initialH = 540;
      initialW = initialH * recAspect;
    }

    initialSizeRef.current = { width: initialW, height: initialH };

    const player = new rrwebPlayer({
      target: containerRef.current,
      props: {
        events,
        autoPlay: false,
        showController: false,
        width: initialW,
        height: initialH,
        maxScale: 1,
      },
    });

    playerRef.current = player;
    lastSyncMs.current = 0;
    wasPlayingRef.current = false;

    // Initial size from wrapper parent
    const parent = wrapperRef.current.parentElement;
    if (parent) {
      applyScale(parent.clientWidth, parent.clientHeight);
    }

    return () => {
      try {
        (player as unknown as { destroy?: () => void }).destroy?.();
      } catch (e) { console.warn('rrweb player destroy failed:', e); }
      playerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [events]);

  // ResizeObserver for container-based sizing
  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const parent = wrapper.parentElement;
    if (!parent) return;

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry || !initialSizeRef.current) return;
      applyScale(entry.contentRect.width, entry.contentRect.height);
    });

    observer.observe(parent);
    return () => observer.disconnect();
  }, [applyScale, events]);

  // Sync playback with audio — play/pause transitions
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const relativeMs = Math.max(0, (audioCurrentTime - segmentStartOffset) * 1000 + recordingStartOffsetSeconds * 1000);

    if (isPlaying && !wasPlayingRef.current) {
      // Audio started playing — goto position and play
      player.goto(relativeMs, true);
      lastSyncMs.current = relativeMs;
    } else if (!isPlaying && wasPlayingRef.current) {
      // Audio paused — pause rrweb and show correct frame
      player.pause();
      player.goto(relativeMs, false);
      lastSyncMs.current = relativeMs;
    }

    wasPlayingRef.current = isPlaying;
  }, [isPlaying, audioCurrentTime, segmentStartOffset, recordingStartOffsetSeconds]);

  // Sync playback with audio — handle seeks (drift detection)
  useEffect(() => {
    const player = playerRef.current;
    if (!player || !isPlaying) return;

    const relativeMs = Math.max(0, (audioCurrentTime - segmentStartOffset) * 1000 + recordingStartOffsetSeconds * 1000);
    const drift = Math.abs(relativeMs - lastSyncMs.current);

    // Only resync if user seeked (drift > 1s)
    if (drift > 1000) {
      player.goto(relativeMs, true);
      lastSyncMs.current = relativeMs;
    } else {
      // Update tracker without re-syncing (rrweb advances on its own)
      lastSyncMs.current = relativeMs;
    }
  }, [audioCurrentTime, isPlaying, segmentStartOffset, recordingStartOffsetSeconds]);

  if (status === 'loading') {
    return (
      <div className={`flex items-center justify-center h-full text-muted-foreground text-sm ${className}`}>
        Loading recording…
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className={`flex items-center justify-center h-full text-muted-foreground text-sm ${className}`}>
        Failed to load recording
      </div>
    );
  }

  return (
    <div className={`flex items-center justify-center ${className}`}>
      <div ref={wrapperRef} style={{ overflow: 'hidden' }}>
        <div ref={containerRef} />
      </div>
    </div>
  );
};

export default SyncedDomPlayer;
